/**
 * 知識の置き場所ガイド（SSOT）
 *
 * 専用ワークスペースの「知っていること」が置かれる 3 か所（ルール・ドキュメント・長期記憶）の
 * 特徴を構造化データとして持ち、セットアップコンシェルジュの振る舞い指示へ埋め込む文面を
 * そこから生成する。
 *
 * 文面はこのデータからのみ生成し、データと食い違う手書きの記述を持たない。データが実際の
 * アプリの挙動と一致していることは、挙動を持つパッケージ側の parity テスト
 * （desktop-app: 持ち運び・有効条件、agent-core: システムプロンプトへの入り方）が照合する。
 * どちらか一方だけを変えるとテストが落ちる。
 */
function freezeTraits(traits) {
    Object.freeze(traits.visibility);
    Object.freeze(traits.formats);
    Object.freeze(traits.portability);
    return Object.freeze(traits);
}
export const KNOWLEDGE_STORE_TRAITS = Object.freeze({
    rule: freezeTraits({
        id: 'rule',
        label: 'ルール',
        visibility: { kind: 'full-text-every-run' },
        writer: 'user',
        formats: ['text'],
        activation: 'always',
        portability: {
            cloudSaved: true,
            deviceLocal: false,
            pluginDistribution: 'included',
            carriedToNewVersion: true,
        },
    }),
    document: freezeTraits({
        id: 'document',
        label: 'ドキュメント',
        visibility: { kind: 'names-only-read-on-demand' },
        writer: 'user',
        formats: ['text', 'pdf', 'image', 'office-binary'],
        activation: 'always',
        portability: {
            // 登録情報はワークスペース設定（クラウド）にあるが、ファイルの本体は端末に置かれる
            cloudSaved: false,
            deviceLocal: true,
            pluginDistribution: 'included',
            carriedToNewVersion: true,
        },
    }),
    memory: freezeTraits({
        id: 'memory',
        label: '長期記憶',
        visibility: { kind: 'summary-index-every-run-body-on-demand', omitsOlderWhenMany: true },
        writer: 'running-agent',
        formats: ['text'],
        activation: 'opt-in',
        portability: {
            cloudSaved: false,
            deviceLocal: true,
            pluginDistribution: 'lesson-category-optional',
            carriedToNewVersion: false,
        },
    }),
});
export const KNOWLEDGE_CURATION_READ_BUDGET = Object.freeze({
    maxDocumentCharsPerRead: 20_000,
    maxDocumentCharsPerAnalysis: 100_000,
    documentHeadChars: 4_000,
});
// ========================================
// 文面の生成
// ========================================
function describeVisibility(visibility) {
    switch (visibility.kind) {
        case 'full-text-every-run':
            return {
                timing: '全文が毎回の実行（対話・自動実行・WebHook・他ワークスペースからの依頼）のシステムプロンプトに入る',
                cost: '長さがそのまま毎回の入力量になる',
            };
        case 'names-only-read-on-demand':
            return {
                timing: 'ファイル名の一覧だけがシステムプロンプトに入り、本文はエージェントが必要と判断したときに読む',
                cost: '本文の分は読んだときだけかかる',
            };
        case 'summary-index-every-run-body-on-demand':
            return {
                timing: '要約の一覧が毎回システムプロンプトに入り' +
                    (visibility.omitsOlderWhenMany ? '（件数が多いと古い教訓・メモは省略される）' : '') +
                    '、本文はエージェントが必要なときに読む',
                cost: '要約の分だけ毎回かかり、本文は読んだときだけかかる',
            };
    }
}
function describeWriter(writer) {
    switch (writer) {
        case 'user':
            return '利用者（とあなた）が書き換える。実行中のエージェントは書き換えない';
        case 'running-agent':
            return '実行中のエージェント自身が記録・上書き・削除する。古くなっている可能性がある前提で扱われる';
    }
}
const FORMAT_LABELS = {
    text: 'テキスト',
    pdf: 'PDF',
    image: '画像',
    'office-binary': 'Office 文書などのバイナリ',
};
function describeFormats(traits) {
    const listed = traits.formats.map((f) => FORMAT_LABELS[f]).join('・');
    switch (traits.id) {
        case 'rule':
            return `${listed}（テンプレート変数で値を差し込める）`;
        case 'document':
            return `${listed}（出力テンプレートを含む）`;
        case 'memory':
            return `短い要約＋${listed}の本文。カテゴリ（引き継ぎ・作業結果・教訓・メモ）と件数上限がある`;
    }
}
function describeActivation(activation) {
    return activation === 'always' ? '常に有効' : 'ワークスペースごとに有効化する（既定は無効）';
}
function describePortability(traits) {
    const p = traits.portability;
    const parts = [];
    if (p.cloudSaved) {
        parts.push('ワークスペース設定としてクラウドに保存される');
    }
    else if (p.deviceLocal) {
        parts.push(traits.id === 'document'
            ? 'ファイルの本体はこの端末に保存される（登録の一覧はワークスペース設定に含まれる）'
            : 'この端末にだけ保存され、別の端末とは共有されない');
    }
    parts.push(p.pluginDistribution === 'included'
        ? 'プラグイン配布に含まれる'
        : 'プラグイン配布には教訓だけを任意で含められる');
    parts.push(p.carriedToNewVersion ? '新バージョンにも引き継がれる' : '新バージョンには引き継がれない');
    return parts.join('。');
}
function describeStore(traits) {
    const { timing, cost } = describeVisibility(traits.visibility);
    return [
        `  ・${traits.label}`,
        `    - 目に入るタイミング: ${timing}。`,
        `    - 費用: ${cost}。`,
        `    - 書き換える主体: ${describeWriter(traits.writer)}。`,
        `    - 扱える形式: ${describeFormats(traits)}。`,
        `    - 有効条件: ${describeActivation(traits.activation)}。`,
        `    - 持ち運び: ${describePortability(traits)}。`,
    ].join('\n');
}
/**
 * 振る舞い指示へ埋め込む日本語の節（特徴＋判断基準）を返す。純粋関数。
 *
 * ツール名に依存しない（整理用ツールの有無にかかわらず常設するため）。
 * テンプレート変数構文（`{{`）を含まない（ルールの補間対象にならないようにするため）。
 */
export function buildKnowledgePlacementGuide() {
    const { rule, document, memory } = KNOWLEDGE_STORE_TRAITS;
    return [
        `専用ワークスペースの知識は${rule.label}・${document.label}・${memory.label}の 3 か所に置けます。それぞれの特徴は次のとおりです（実際のアプリの挙動です）。`,
        describeStore(rule),
        describeStore(document),
        describeStore(memory),
        'ある知識をどこに置くかは、次の観点で見極めてください: (1) すべての実行で常に必要か、特定の場面でだけ必要か (2) 分量 (3) 利用者が定めるものか、実行の中で変わっていくものか (4) どれだけ長く通用するか (5) ワークスペースと一緒に配布・複製されるべきか (6) 形式（テキストか、PDF・Office 文書などのファイルか）。',
        `振り分けの基準: すべての実行に当てはまる振る舞い指示・判断基準・禁止事項・出力の形式は${rule.label}に置きます。特定の場面でだけ参照する資料・分量の多い参照情報・出力テンプレート・ファイル形式でしか用意できない情報は${document.label}に置きます。実行の中で得られ今後も変わりうる教訓・個別の作業の引き継ぎや進捗・作業結果は${memory.label}に置きます。複数のワークスペースで使い回す手順はスキルの置き場所です（スキルはワークスペースをまたいで共有され、各ワークスペースで有効化します）。`,
        `移す案を出す状況: (a) ${memory.label}にある教訓が、実行をまたいで繰り返し確認され恒常的に通用すると判断できるときは、${rule.label}または${document.label}へ移す案を出します。(b) ${rule.label}に分量の多い参照情報や特定の場面でしか使わない手順が含まれているときは、それを${document.label}へ移し、${rule.label}には参照先と使う場面だけを残す案を出します。(c) ワークスペースと一緒に配布・複製されるべき知識が${memory.label}にしか無いときは、${memory.label}は新バージョンに引き継がれず端末をまたがないことを理由に、${rule.label}または${document.label}へ移す案を出します。`,
        `${memory.label}が無効なワークスペースでは、${memory.label}に置くのが適切な知識であっても${memory.label}を置き場所に選ばず、${memory.label}の有効化を選択肢として示してください。`,
        '置き場所を提案するときは、その置き場所を選んだ理由を上の特徴（目に入るタイミング・費用・書き換える主体・形式・有効条件・持ち運び）に結びつけて示してください。この判断基準は、整理の依頼に限らず、ワークスペースの新規作成・既存ワークスペースの変更・改善提案・利用者からの置き場所の相談のいずれでも用います。',
        `${rule.label}と自動実行の prompt のどちらに書くかは【rule と自動実行の prompt の書き分け】に従います。この判断基準は、${rule.label}に置くと決めた内容をさらに自動実行の prompt と書き分ける基準と矛盾せず、両方を併せて用います（すべての実行に当てはまる指示は${rule.label}、そのスケジュールの実行にだけ当てはまる指示は prompt）。`,
    ].join('\n');
}
//# sourceMappingURL=knowledge-placement-guide.js.map